import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Clock, Film, Ticket, MapPin, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { getFullProgram, type PublicFilm } from "@/lib/films.functions";
import { getFilmActivities, type PublicActivity } from "@/lib/film-activities.functions";
import { FilmActivitiesSection } from "@/components/festival/FilmActivities";
import { getTicketsSettings, type TicketsSettings } from "@/lib/tickets.functions";
import { RichText } from "@/components/admin/RichText";
import { TrailerController, TrailerSection } from "@/components/festival/trailer/TrailerSection";

const BASE_URL = "https://openluchtfilmfestival.nl";

function slugify(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export const Route = createFileRoute("/films/$slug")({
  loader: async ({ params }) => {
    const films = await getFullProgram();
    const film = films.find((f) => slugify(f.title) === params.slug);
    if (!film) throw notFound();
    return { film };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Film niet gevonden — Openluchtfilmfestival Apeldoorn" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { film } = loaderData;
    const title = `${film.title} — Openluchtbioscoop Apeldoorn 2026`;
    const description =
      film.teaser?.trim() ||
      `Bekijk ${film.title} op het Openluchtfilmfestival Apeldoorn 2026 — een openluchtbioscoop-avond onder de sterren. Filmavond in Apeldoorn met eten, drinken en sfeer.`;
    const url = `${BASE_URL}/films/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "video.movie" },
        { property: "og:url", content: url },
        ...(film.poster_url
          ? [{ property: "og:image", content: film.poster_url }]
          : []),
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ScreeningEvent",
            name: `${film.title} — Openluchtbioscoop Apeldoorn`,
            description,
            startDate: film.start_time
              ? `${film.play_date}T${film.start_time}`
              : film.play_date,
            eventAttendanceMode:
              "https://schema.org/OfflineEventAttendanceMode",
            eventStatus: "https://schema.org/EventScheduled",
            location: {
              "@type": "Place",
              name: "Openluchtfilmfestival Apeldoorn",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Apeldoorn",
                addressCountry: "NL",
              },
            },
            ...(film.poster_url ? { image: film.poster_url } : {}),
            workPresented: {
              "@type": "Movie",
              name: film.title,
              ...(film.genre ? { genre: film.genre } : {}),
            },
            ...(film.cta_url ? { url: film.cta_url } : {}),
          }),
        },
      ],
    };
  },
  notFoundComponent: FilmNotFound,
  errorComponent: FilmError,
  component: FilmDetailPage,
});

const WEEKDAYS_NL = [
  "zondag",
  "maandag",
  "dinsdag",
  "woensdag",
  "donderdag",
  "vrijdag",
  "zaterdag",
];
const MONTHS_NL = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

function formatLong(iso: string) {
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, (m ?? 1) - 1, d ?? 1);
  return `${WEEKDAYS_NL[date.getDay()]} ${date.getDate()} ${MONTHS_NL[date.getMonth()]}`;
}

function FilmDetailPage() {
  const { film } = Route.useLoaderData();
  const [tickets, setTickets] = useState<TicketsSettings>({ enabled: false, url: null });
  const [otherFilms, setOtherFilms] = useState<PublicFilm[]>([]);
  const [activities, setActivities] = useState<PublicActivity[]>([]);

  useEffect(() => {
    getTicketsSettings().then(setTickets).catch(() => {});
    getFullProgram()
      .then((all) => setOtherFilms(all.filter((f) => f.id !== film.id).slice(0, 6)))
      .catch(() => {});
    getFilmActivities()
      .then((map) => setActivities(map[film.id] ?? []))
      .catch(() => {});
  }, [film.id]);

  const filmUrl = film.cta_url && /^https?:\/\//i.test(film.cta_url) ? film.cta_url : null;
  const fallback = tickets.url && /^https?:\/\//i.test(tickets.url) ? tickets.url : null;
  const ticketUrl = filmUrl ?? fallback;
  const ticketLabel = film.cta_label?.trim() || "Koop tickets";

  return (
    <>
      <Nav />
      <main className="min-h-screen bg-night-deep pt-28 md:pt-32">
        <section className="relative py-10 md:py-14">
          <div className="mx-auto max-w-6xl px-5 md:px-8">
            <Link
              to="/tickets"
              className="inline-flex items-center gap-2 font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/60 hover:text-gold"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Alle films
            </Link>

            <div className="mt-8 grid gap-10 md:grid-cols-[minmax(0,1fr)_1.4fr] md:gap-14">
              <div className="relative overflow-hidden rounded-2xl border border-border bg-card">
                <div className="relative aspect-[2/3] bg-night-deep">
                  {film.poster_url && (
                    <img
                      src={film.poster_url}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-2xl"
                    />
                  )}
                  {film.poster_url ? (
                    <img
                      src={film.poster_url}
                      alt={`Poster van ${film.title}`}
                      className="relative h-full w-full object-contain"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">
                      <Film className="h-16 w-16 text-gold/40" />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="font-mono-cinema text-[10px] uppercase tracking-wider text-gold">
                  Openluchtbioscoop Apeldoorn · editie 2026
                </p>
                <h1 className="mt-4 font-display text-4xl leading-[1.05] md:text-5xl lg:text-6xl">
                  {film.title}
                </h1>
                {film.teaser && (
                  <p className="mt-5 text-lg text-foreground/80">{film.teaser}</p>
                )}

                <dl className="mt-8 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                  <div className="flex items-start gap-2">
                    <Calendar className="mt-0.5 h-4 w-4 text-gold" />
                    <div>
                      <dt className="font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/60">Speeldatum</dt>
                      <dd className="text-foreground/90">{formatLong(film.play_date)}</dd>
                    </div>
                  </div>
                  {film.start_time && (
                    <div className="flex items-start gap-2">
                      <Clock className="mt-0.5 h-4 w-4 text-gold" />
                      <div>
                        <dt className="font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/60">Doek op</dt>
                        <dd className="text-foreground/90">{film.start_time}</dd>
                      </div>
                    </div>
                  )}
                  {film.genre && (
                    <div className="flex items-start gap-2">
                      <Film className="mt-0.5 h-4 w-4 text-gold" />
                      <div>
                        <dt className="font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/60">Genre</dt>
                        <dd className="text-foreground/90">{film.genre}</dd>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-2">
                    <MapPin className="mt-0.5 h-4 w-4 text-gold" />
                    <div>
                      <dt className="font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/60">Locatie</dt>
                      <dd className="text-foreground/90">Apeldoorn — openlucht</dd>
                    </div>
                  </div>
                  {film.duration_minutes && (
                    <div className="flex items-start gap-2">
                      <Clock className="mt-0.5 h-4 w-4 text-gold" />
                      <div>
                        <dt className="font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/60">Duur</dt>
                        <dd className="text-foreground/90">{film.duration_minutes} min</dd>
                      </div>
                    </div>
                  )}
                  {film.age_rating && (
                    <div className="flex items-start gap-2">
                      <Film className="mt-0.5 h-4 w-4 text-gold" />
                      <div>
                        <dt className="font-mono-cinema text-[10px] uppercase tracking-wider text-foreground/60">Kijkwijzer</dt>
                        <dd className="text-foreground/90">{film.age_rating}</dd>
                      </div>
                    </div>
                  )}
                </dl>

                <TrailerController>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  {ticketUrl ? (
                    <Button asChild variant="hero" size="lg">
                      <a href={ticketUrl} target="_blank" rel="noreferrer noopener">
                        <Ticket className="h-4 w-4" />
                        {ticketLabel}
                      </a>
                    </Button>
                  ) : (
                    <Button variant="ghostLine" size="lg" disabled>
                      Binnenkort te koop
                    </Button>
                  )}
                  <Button asChild variant="ghostLine" size="lg">
                    <Link to="/programma" hash={`film-${film.id}`}>
                      Bekijk programma
                    </Link>
                  </Button>
                  <TrailerSection
                    filmId={film.id}
                    filmTitle={film.title}
                    trailerUrl={film.trailer_url}
                  />
                </div>
                </TrailerController>

                {film.description && (
                  <RichText html={film.description} className="mt-10 text-foreground/80" />
                )}

                <FilmActivitiesSection items={activities} />

                <section className="mt-12 rounded-2xl border border-border bg-card p-6 md:p-8">
                  <h2 className="font-display text-2xl">
                    {film.title} in de <span className="italic text-gold">openluchtbioscoop</span>
                  </h2>
                  <p className="mt-3 text-foreground/75">
                    Beleef {film.title} op groot doek in de buitenlucht tijdens
                    het Openluchtfilmfestival Apeldoorn. Een unieke filmavond in
                    Apeldoorn met een sfeervolle setting, lokale horeca en het
                    grootste doek van de Veluwe. Neem een dekentje mee — de
                    projectie start bij zonsondergang.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </section>

        {otherFilms.length > 0 && (
          <section className="relative border-t border-border py-16">
            <div className="mx-auto max-w-6xl px-5 md:px-8">
              <h2 className="font-display text-3xl">Meer films dit festival</h2>
              <ul className="mt-8 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-6">
                {otherFilms.map((o) => (
                  <li key={o.id}>
                    <Link
                      to="/films/$slug"
                      params={{ slug: slugify(o.title) }}
                      className="group block"
                    >
                      <div className="relative aspect-[2/3] overflow-hidden rounded-lg border border-border bg-night-deep">
                        {o.poster_url ? (
                          <img
                            src={o.poster_url}
                            alt={o.title}
                            className="h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <Film className="h-8 w-8 text-gold/40" />
                          </div>
                        )}
                      </div>
                      <p className="mt-2 font-display text-sm leading-tight group-hover:text-gold">
                        {o.title}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

function FilmNotFound() {
  return (
    <>
      <Nav />
      <main className="min-h-screen bg-night-deep pt-32">
        <div className="mx-auto max-w-xl px-5 py-20 text-center">
          <Film className="mx-auto h-12 w-12 text-gold/60" />
          <h1 className="mt-6 font-display text-3xl">Film niet gevonden</h1>
          <p className="mt-3 text-foreground/70">
            Deze film staat niet (meer) in het programma.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button asChild variant="hero">
              <Link to="/tickets">Alle films</Link>
            </Button>
            <Button asChild variant="ghostLine">
              <Link to="/">Naar home</Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function FilmError({ reset }: { reset: () => void }) {
  return (
    <>
      <Nav />
      <main className="min-h-screen bg-night-deep pt-32">
        <div className="mx-auto max-w-xl px-5 py-20 text-center">
          <h1 className="font-display text-3xl">Er ging iets mis</h1>
          <p className="mt-3 text-foreground/70">
            De film kon niet worden geladen. Probeer het opnieuw.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="hero" onClick={reset}>Opnieuw proberen</Button>
            <Button asChild variant="ghostLine">
              <Link to="/tickets">Alle films</Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}